import { type ChangeEvent, type ComponentProps, useId, useState } from "react";
import type { FieldError } from "react-hook-form";
import { cn } from "../lib/utils";
import { InlineFeedback, InlineFeedbackGroup } from "./inline-feedback";

type TextareaProps = ComponentProps<"textarea"> & {
  helperText?: string;
  error?: FieldError;
  showCounter?: boolean;
  maxLength?: number;
  useLabel?: boolean;
};
type TextareaWithRefProps = TextareaProps & {
  ref?: React.Ref<HTMLTextAreaElement>;
};
const Textarea = ({
  className,
  helperText,
  placeholder,
  id,
  error,
  disabled,
  showCounter = false,
  maxLength = 300,
  useLabel = false,
  value,
  onBlur,
  ref,
  ...restProps
}: TextareaWithRefProps) => {
  const textareaId = id || useId();
  const [charCount, setCharCount] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const hasContent = Boolean(value && String(value).trim().length > 0);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setCharCount(e.target.value.length);
  };
  const handleFocus = () => {
    setIsFocused(true);
  };
  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false);
    if (onBlur) {
      onBlur(e);
    }
  };
  return (
    <div className="flex flex-col space-y-1">
      <div
        className={cn(
          "relative flex flex-col border rounded bg-transparent transition-colors bg-white",
          error ? "border-red-500" : "border-gray-200",
          !disabled && !error
            ? "hover:border-pink-200 focus-within:border-pink-300"
            : "",
          disabled
            ? [
                "cursor-not-allowed",
                "bg-gray-50 text-gray-700 placeholder:text-gray-500 border-gray-200",
              ]
            : "",
          className,
        )}
      >
        <div className="flex items-center justify-between select-none">
          {useLabel && placeholder ? (
            <label
              htmlFor={textareaId}
              className="px-4 mt-2 text-caption text-gray-700"
            >
              {placeholder}
            </label>
          ) : null}
          {showCounter && (
            <span className="pl-4 mt-2 !text-caption text-gray-700">
              {charCount}/{maxLength}
            </span>
          )}
        </div>
        <textarea
          ref={ref}
          id={textareaId}
          {...(!useLabel && { placeholder })}
          disabled={disabled}
          maxLength={maxLength}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          value={value}
          style={{
            height: isFocused || hasContent ? "200px" : "48px",
            transition: "height 0.3s ease",
          }}
          className={cn(
            "w-full px-4 pt-2 text-caption bg-transparent rounded placeholder:text-gray-700",
            "focus-visible:outline-none resize-none overflow-y-auto",
            disabled ? "opacity-50 cursor-not-allowed" : "",
            className,
          )}
          {...restProps}
        />
      </div>

      {(error?.message || helperText) && (
        <InlineFeedbackGroup>
          {error?.message && (
            <InlineFeedback variant="critical" message={error.message} nested />
          )}
          {helperText && (
            <InlineFeedback variant="info" message={helperText} nested />
          )}
        </InlineFeedbackGroup>
      )}
    </div>
  );
};
Textarea.displayName = "Textarea";
export { Textarea };
